/**
 * WHAT THE COMPOSER DOES TO A FILE BEFORE IT SENDS IT (ART-159, C-Attach).
 *
 * Pure-ish helpers, out of the component so the RULES are assertable without a DOM event: what we accept,
 * what an image is downscaled to, and what a rejected file is called.
 *
 * **The client's caps are not the server's, and the gap is deliberate.** The server allowlists the types and
 * caps the bytes (`ChatAttachment`); these numbers are what a PERSON is told about, checked before a 10 MB
 * photo is pushed up a hotel wifi to be refused. A client one release behind still cannot build a request
 * the server accepts and shouldn't — the same two-numbers arrangement as the message-length cap.
 */

/** The types the picker offers, mirroring the server's allowlist. A file dialog filter, not a gate. */
export const ATTACHMENT_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv";

/** 10 MB, the server's cap on the DECODED bytes — checked here so the refusal arrives before the upload. */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * The longest edge an image is downscaled to before sending, and the JPEG quality — the spec's own
 * 1600px / q0.85.
 *
 * It is an OPTIMISATION rather than a rule: the server caps the bytes and validates the type either way, so
 * a client that cannot run a canvas (or a format that will not decode) sends the original and is accepted or
 * refused on its own merits. That is why every failure path here returns the file UNCHANGED rather than
 * throwing.
 */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/** True for a type the server will take as an `image` — the kinds worth downscaling. */
function isDownscalable(type: string): boolean {
  // GIF is excluded on purpose: a canvas re-encode keeps the FIRST FRAME and silently throws the animation
  // away, which is most of why somebody sent one.
  return type === "image/jpeg" || type === "image/png" || type === "image/webp";
}

/**
 * Downscale an image to {@link MAX_EDGE} on its longest side, as a JPEG. Returns the ORIGINAL file
 * unchanged when it is not a downscalable image, is already small enough, or cannot be decoded — an
 * optimisation that fails must never cost the send.
 *
 * **A PNG becomes a JPEG, and only when it was actually too big.** Re-encoding a small transparent PNG to
 * fill its background with black is a worse outcome than sending the 200 KB it already was.
 */
export async function compressImage(file: File): Promise<File> {
  if (!isDownscalable(file.type)) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1) {
      bitmap.close?.();
      return file;   // already inside the box; re-encoding would only lose quality
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob) return file;
    return new File([blob], jpegName(file.name), { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    // No createImageBitmap, no canvas, an undecodable image — send the original and let the server decide.
    return file;
  }
}

/** `tour-poster.png` → `tour-poster.jpg`, so the name a reader downloads matches the bytes they get. */
function jpegName(name: string): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem || "image"}.jpg`;
}

/**
 * Why this file cannot be sent, or null. ONE sentence per reason and each names what to do instead — a
 * refusal that only says "invalid" is one the sender cannot act on.
 */
export function attachmentRefusal(file: File): string | null {
  if (file.size === 0) return "That file is empty.";
  // Checked on the ORIGINAL, before compression, because a 40 MB photo should be refused rather than spend
  // ten seconds in a canvas to arrive under the cap. A downscale that brings it under is a bonus, not a
  // reason to accept anything.
  if (file.size > ATTACHMENT_MAX_BYTES && !isDownscalable(file.type)) {
    return `Files are at most ${ATTACHMENT_MAX_BYTES / (1024 * 1024)} MB.`;
  }
  if (!ATTACHMENT_ACCEPT.split(",").includes(file.type)) {
    return "Attach an image, a PDF, or a text file.";
  }
  return null;
}
